import { Tune } from "@mui/icons-material";
import {
  Checkbox,
  Option,
  Select,
  Stack,
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
} from "@mui/joy";
import { Outlet } from "@tanstack/react-router";
import { ApiRoutesTargetConfig } from "griptonite-api-client";
import { useEffect, useState } from "react";

import { useTargets } from "@/hooks/targets";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { useModal } from "../modals/useModal";
import GenerateTargetsForm from "./GenerateTargetsForm";
import TargetsGraph from "./TargetsGraph";
import TargetsTable from "./TargetsTable";

const EmptyTargetsState = ({ climbType }: { climbType: "BOULDER" | "SPORT" }) => {
  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          filter: "blur(3px)",
          opacity: 0.3,
          zIndex: 1,
          pointerEvents: "none",
        }}
      >
        <Box sx={{ height: 400 }}>
          <TargetsGraph climbType={climbType} displayMockData />
        </Box>
        <TargetsTable climbType={climbType} showAllGrades={true} displayMockData />
      </Box>

      <Card
        sx={{
          zIndex: 2,
          maxWidth: 450,
          boxShadow: "lg",
        }}
      >
        <CardContent sx={{ padding: 1 }}>
          <Box mb={2}>
            <Typography level="h4">Targets not configured</Typography>
            <Typography level="body-md">
              Let’s set some targets using your current routes as a starting point.
            </Typography>
          </Box>

          <GenerateTargetsForm climbType={climbType} />
        </CardContent>
      </Card>
    </Box>
  );
};

const Controls = ({
  groupBy,
  setGroupBy,
  mode,
  showAllGrades,
  setShowAllGrades,
  config,
  climbType,
}: {
  groupBy: "zone" | "gradeValue" | "ascentStyle" | null;
  setGroupBy: (value: "zone" | "gradeValue" | "ascentStyle" | null) => void;
  mode: string | undefined;
  showAllGrades: boolean;
  setShowAllGrades: (value: boolean) => void;
  config: ApiRoutesTargetConfig | undefined;
  climbType: "BOULDER" | "SPORT";
}) => {
  const { display: displayModal } = useModal();

  const showSettingsModal = () => {
    displayModal("TARGETS_SETTINGS", { climbType, config });
  };

  return (
    <Stack
      direction="row"
      padding={1}
      sx={{
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
      spacing={2}
      minHeight={50}
    >
      {mode !== "GLOBAL" ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography>Group by</Typography>
          <Select
            value={groupBy || ("" as const)}
            onChange={(_, value) => setGroupBy(value || null)}
          >
            <Option value="">None</Option>
            {mode === "BY_ZONE" ? <Option value="zone">Zone</Option> : null}
            {mode === "BY_ASCENT_STYLE" ? <Option value="ascentStyle">Ascent style</Option> : null}
            <Option value="gradeValue">Grade</Option>
          </Select>
        </Stack>
      ) : null}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Checkbox
          checked={showAllGrades}
          onChange={(event) => setShowAllGrades(event.target.checked)}
          label="Show all grades"
        />
      </Stack>
      <Box flex={1} />
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography level="body-sm">
          Tolerance: <strong>±{config?.tolerance ?? "-"}</strong>
        </Typography>
        <Typography level="body-sm">
          Warning threshold: <strong>±{config?.warningThreshold ?? "-"}</strong>
        </Typography>
        <IconButton onClick={showSettingsModal}>
          <Tune fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
};

export default function Targets({ climbType }: { climbType: "BOULDER" | "SPORT" }) {
  const [groupBy, setGroupBy] = useState<"zone" | "gradeValue" | "ascentStyle" | null>(null);
  const [showAllGrades, setShowAllGrades] = useState(false);

  const venueUuid = useCurrentVenueUuid();
  const { data: targetsResponse } = useTargets(venueUuid, climbType);

  const config = targetsResponse?.config;
  const mode = config?.mode;
  const targets = targetsResponse?.targets || [];
  const hasTargets = targets.length > 0;

  useEffect(() => {
    if (mode === "BY_ZONE") {
      setGroupBy("zone");
    } else if (mode === "BY_ASCENT_STYLE") {
      setGroupBy("ascentStyle");
    } else {
      setGroupBy(null);
    }
  }, [mode]);

  // Show empty state if targetsResponse has loaded but no targets exist
  if (targetsResponse && !hasTargets) {
    return (
      <Stack direction="row" sx={{ height: "100dvh" }}>
        <Stack flex={1}>
          <Controls
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            mode={mode}
            showAllGrades={showAllGrades}
            setShowAllGrades={setShowAllGrades}
            config={config}
            climbType={climbType}
          />
          <EmptyTargetsState climbType={climbType} />
        </Stack>
        <Outlet />
      </Stack>
    );
  }

  return (
    <Stack direction="row" sx={{ height: "100dvh" }}>
      <Stack flex={1} sx={{ minWidth: 0 }}>
        <Controls
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          mode={mode}
          config={config}
          showAllGrades={showAllGrades}
          setShowAllGrades={setShowAllGrades}
          climbType={climbType}
        />
        <Box sx={{ height: 400 }}>
          <TargetsGraph climbType={climbType} showAllGrades={showAllGrades} />
        </Box>
        <TargetsTable
          climbType={climbType}
          groupBy={groupBy ?? undefined}
          showAllGrades={showAllGrades}
        />
      </Stack>
      <Outlet />
    </Stack>
  );
}
